import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

const SELECTORS = {
	menuContent: '[id^="MegaMenu-Content"]',
	hoverImage: '[data-uc-mega-menu-hover-image]',
	imageLink: '[data-uc-mega-menu-image]',
} as const

const MAX_PREWARMED_IMAGES = 2

type ImagePriority = 'high' | 'low'

type Preload = {
	image: HTMLImageElement
	loadedUrl: Promise<string>
}

type NetworkInformation = {
	effectiveType?: string
	saveData?: boolean
}

export class MegaMenuHoverMedia extends UcoastEl {
	static htmlSelector = 'mega-menu-hover-media'

	hoverImage?: HTMLImageElement
	hoveredLink?: HTMLAnchorElement
	focusedLink?: HTMLAnchorElement
	requestId = 0
	preloads = new Map<string, Preload>()
	previewPreloads = new Map<string, Preload>()
	prewarmedImages = new Set<string>()
	menuContent?: HTMLElement
	menuObserver?: MutationObserver

	constructor() {
		super()
		this.handleImageEnter = this.handleImageEnter.bind(this)
		this.handleImageLeave = this.handleImageLeave.bind(this)
	}

	override connectedCallback() {
		super.connectedCallback()
		this.hoverImage = q.os<HTMLImageElement>(SELECTORS.hoverImage, this)
		if (!this.hoverImage) return
		this.menuContent =
			this.closest<HTMLElement>(SELECTORS.menuContent) ?? undefined

		this.addEventListener('mouseover', this.handleImageEnter)
		this.addEventListener('focusin', this.handleImageEnter)
		this.addEventListener('mouseout', this.handleImageLeave)
		this.addEventListener('focusout', this.handleImageLeave)
		if (this.menuContent) {
			this.menuObserver = new MutationObserver(() => this.handleMenuVisibility())
			this.menuObserver.observe(this.menuContent, {
				attributeFilter: ['hidden'],
				attributes: true,
			})
			this.handleMenuVisibility()
		}
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('mouseover', this.handleImageEnter)
		this.removeEventListener('focusin', this.handleImageEnter)
		this.removeEventListener('mouseout', this.handleImageLeave)
		this.removeEventListener('focusout', this.handleImageLeave)
		this.menuObserver?.disconnect()
		this.menuObserver = undefined
	}

	handleImageEnter(event: Event) {
		const link = q.oClosestTarget<Event, HTMLAnchorElement>(
			event,
			SELECTORS.imageLink
		)
		if (!link || !this.hoverImage) return
		const relatedTarget =
			event instanceof MouseEvent ? event.relatedTarget : null
		if (relatedTarget instanceof Node && link.contains(relatedTarget))
			return

		if (event.type === 'mouseover') {
			this.hoveredLink = link
		} else {
			this.focusedLink = link
		}

		void this.showImage(link)
		this.scheduleNearbyPrewarm(link)
	}

	async showImage(link: HTMLAnchorElement) {
		if (!this.hoverImage) return

		const image = link.dataset.ucMegaMenuImage
		const srcset = link.dataset.ucMegaMenuImageSrcset
		if (!image || !srcset) return

		const requestId = ++this.requestId
		this.hoverImage.dataset.requestedImage = image

		const preview = this.loadPreview(link, 'high')
		const fullImage = this.loadImage(link, 'high')
		try {
			const previewUrl = await preview
			if (requestId !== this.requestId || !this.hoverImage) return
			this.displayImage(link, previewUrl)
		} catch {
			// The full image can still complete if its lightweight preview fails.
		}
		try {
			const loadedUrl = await fullImage
			if (requestId !== this.requestId || !this.hoverImage) return
			this.displayImage(link, loadedUrl)
		} catch {
			// Keep the previous ready image, or the promo before the first successful load.
		}
	}

	displayImage(link: HTMLAnchorElement, url: string) {
		if (!this.hoverImage) return
		this.hoverImage.src = url
		this.hoverImage.style.objectPosition =
			link.dataset.ucMegaMenuImagePosition || 'center'
		this.hoverImage.setAttribute('data-active', '')
	}

	handleImageLeave(event: Event) {
		const link = q.oClosestTarget<Event, HTMLAnchorElement>(
			event,
			SELECTORS.imageLink
		)
		if (!link || !this.hoverImage) return

		const relatedTarget =
			event instanceof MouseEvent || event instanceof FocusEvent
				? event.relatedTarget
				: null
		const nextLink =
			relatedTarget instanceof Element
				? relatedTarget.closest<HTMLAnchorElement>(SELECTORS.imageLink)
				: null
		if (nextLink === link) return

		if (event.type === 'mouseout') {
			this.hoveredLink =
				nextLink && this.contains(nextLink) ? nextLink : undefined
		} else {
			this.focusedLink =
				nextLink && this.contains(nextLink) ? nextLink : undefined
		}

		const activeLink = this.hoveredLink || this.focusedLink
		if (activeLink) {
			void this.showImage(activeLink)
			return
		}

		this.resetHoverImage()
	}

	handleMenuVisibility() {
		if (!this.menuContent?.hidden) {
			this.prewarmPreviews()
			return
		}
		this.hoveredLink = undefined
		this.focusedLink = undefined
		this.resetHoverImage()
	}

	resetHoverImage() {
		this.requestId += 1
		if (!this.hoverImage) return
		delete this.hoverImage.dataset.requestedImage
		this.hoverImage.removeAttribute('data-active')
		this.hoverImage.removeAttribute('src')
	}

	prewarmPreviews() {
		if (this.shouldAvoidPrewarm()) return
		for (const link of q.ol<HTMLAnchorElement>(SELECTORS.imageLink, this) || []) {
			void this.loadPreview(link, 'low').catch(() => undefined)
		}
	}

	loadPreview(link: HTMLAnchorElement, priority: ImagePriority) {
		const src = link.dataset.ucMegaMenuImagePreview
		if (!src) return this.loadImage(link, priority)
		if (priority === 'high') {
			for (const preload of this.previewPreloads.values()) {
				preload.image.fetchPriority = 'low'
			}
		}
		const existing = this.previewPreloads.get(src)
		if (existing) {
			if (priority === 'high') existing.image.fetchPriority = 'high'
			return existing.loadedUrl
		}

		const image = new Image()
		image.fetchPriority = priority
		image.src = src
		const loadedUrl = image
			.decode()
			.then(() => image.currentSrc || image.src)
			.catch((error: unknown) => {
				this.previewPreloads.delete(src)
				throw error
			})
		this.previewPreloads.set(src, { image, loadedUrl })
		return loadedUrl
	}

	loadImage(link: HTMLAnchorElement, priority: ImagePriority) {
		const src = link.dataset.ucMegaMenuImage
		const srcset = link.dataset.ucMegaMenuImageSrcset
		if (!src || !srcset)
			return Promise.reject(new Error('Missing image data'))

		const width = Math.max(
			1,
			Math.ceil(this.hoverImage?.getBoundingClientRect().width || 1)
		)
		const key = `${srcset}|${width}`
		if (priority === 'high') {
			for (const preload of this.preloads.values()) {
				preload.image.fetchPriority = 'low'
			}
		}
		const existing = this.preloads.get(key)
		if (existing) {
			if (priority === 'high') existing.image.fetchPriority = 'high'
			return existing.loadedUrl
		}

		const image = new Image()
		image.fetchPriority = priority
		image.sizes = `${width}px`
		image.srcset = srcset
		image.src = src
		const loadedUrl = image
			.decode()
			.then(() => image.currentSrc || image.src)
			.catch((error: unknown) => {
				this.preloads.delete(key)
				throw error
			})
		this.preloads.set(key, { image, loadedUrl })
		return loadedUrl
	}

	scheduleNearbyPrewarm(activeLink: HTMLAnchorElement) {
		if (
			this.prewarmedImages.size >= MAX_PREWARMED_IMAGES ||
			this.shouldAvoidPrewarm()
		)
			return

		const links = Array.from(
			q.ol<HTMLAnchorElement>(SELECTORS.imageLink, this) || []
		)
		const activeIndex = links.indexOf(activeLink)
		const nearbyLinks = [
			links[activeIndex - 1],
			links[activeIndex + 1],
		].filter((link): link is HTMLAnchorElement => Boolean(link))

		const prewarm = () => {
			if (
				!this.isConnected ||
				(this.hoveredLink || this.focusedLink) !== activeLink ||
				this.shouldAvoidPrewarm()
			)
				return

			for (const link of nearbyLinks) {
				const src = link.dataset.ucMegaMenuImage
				if (!src || this.prewarmedImages.has(src)) continue
				if (this.prewarmedImages.size >= MAX_PREWARMED_IMAGES) return

				this.prewarmedImages.add(src)
				void this.loadImage(link, 'low').catch(() => undefined)
			}
		}

		if (typeof window.requestIdleCallback === 'function') {
			window.requestIdleCallback(prewarm, { timeout: 1000 })
		} else {
			setTimeout(prewarm, 0)
		}
	}

	shouldAvoidPrewarm() {
		const connection = (
			navigator as Navigator & { connection?: NetworkInformation }
		).connection
		return (
			connection?.saveData === true ||
			connection?.effectiveType === 'slow-2g' ||
			connection?.effectiveType === '2g'
		)
	}
}
